import React, { useState } from "react";

import styled, { ThemeContext } from "styled-components";

import {
  DARK_THEME_POPPER_PORTAL_ID,
  DEFAULT_THEME_POPPER_PORTAL_ID
} from "../../../constants";
import { BindingShape, InputParameter, ParameterType } from "../../../types";
import { useStableSpaceContext } from "../../spaces/SpaceRoot/SpaceContext";
import { ModalNew as BaseModalNew } from "../Modal";
import { BindingSchema } from "../schema";

import FormBuilder from "./FormBuilder";
import {
  Action,
  ActionType,
  FormState,
  InternalField,
  reducerFactory
} from "./reducer";
import { FunctionNode } from "./types";
import { ALL_SUPPORTED_PARAMETER_TYPES, toInputParameters } from "./utils";

export const ModalNew = styled(BaseModalNew)`
  .ant-modal-body {
    padding: 25px 32px 0 32px
    margin-right: -32px;

    > * {
      padding-right: 32px;
    }
  }

  .ant-modal-footer {
    padding: 20px 32px;
  }
`;
export interface Props {
  func: FunctionNode;
  title: string;
  initialInputParameters?: InputParameter[];
  schema?: Record<string, BindingSchema>;
  onSave?: (inputParameters: InputParameter[]) => void;
  onCancel?: () => void;
  allowedParameterTypes?: ParameterType[];
  internalFields?: InternalField[];
  customReducer?: (state: FormState, action: Action) => FormState;
  visible?: boolean;
  allowedBindingShapes?: BindingShape[];
}

export default function FormBuilderModal({
  func,
  onCancel,
  title,
  schema = {},
  initialInputParameters = [],
  onSave = () => null,
  allowedParameterTypes = ALL_SUPPORTED_PARAMETER_TYPES,
  internalFields,
  customReducer,
  visible = true,
  allowedBindingShapes = [BindingShape.SCALAR]
}: Props) {
  const theme = React.useContext(ThemeContext);
  const { findSpaceComponentPackage } = useStableSpaceContext();
  const [reducer] = useState(() =>
    reducerFactory(findSpaceComponentPackage, customReducer)
  );

  const [state, dispatch] = React.useReducer(reducer, {
    initialInputParameters,
    schema,
    parameters: [],
    parameterErrors: {},
    allowedParameterTypes,
    internalFields: internalFields || []
  });

  React.useEffect(() => {
    dispatch({
      type: ActionType.LOAD_FUNCTION,
      payload: {
        func,
        allowedParameterTypes
      }
    });
  }, [func, allowedParameterTypes]);

  const container =
    theme.id === "dark"
      ? (document.getElementById(DARK_THEME_POPPER_PORTAL_ID) as HTMLElement)
      : (document.getElementById(DEFAULT_THEME_POPPER_PORTAL_ID) as HTMLElement);
  return (
    <ModalNew
      getContainer={container}
      visible={visible}
      onCancel={onCancel}
      onOk={() => {
        if (
          Object.entries(state.parameterErrors).find(
            ([_paramName, hasError]: [string, boolean]) => {
              return !!hasError;
            }
          )
        ) {
          return;
        }
        onSave(toInputParameters(state));
      }}
      width={900}
    >
      <FormBuilder
        title={title}
        dispatch={dispatch}
        state={state}
        allowedParameterTypes={allowedParameterTypes}
        allowedBindingShapes={allowedBindingShapes}
      />
    </ModalNew>
  );
}
